<template>
  <div class="ocr-page-section">
    <div class="ocr-tabbed-stack">
      <OcrTabs v-model="uploadTab">
        <v-tab value="selection">File Selection</v-tab>
        <v-tab value="excel">Excel File Selection</v-tab>
      </OcrTabs>

      <v-window v-model="uploadTab" class="ocr-tab-window">
        <v-window-item value="selection">
          <OcrUploadPanel
            v-model="pickedFiles"
            :uploading="uploading"
            :upload-error="uploadError"
            @upload="uploadFiles"
          />

          <OcrUploadFileGrid
            v-model:selected="selectedUploaded"
            :refresh-key="listKey"
            :processing-file="processingOneFile || jobStatus?.current_file"
            @process-one="processOne"
            @error="gridError = $event"
          />

          <OcrProcessStatus v-if="jobStatus" :job="jobStatus" />

          <div class="ocr-upload-workspace-actions ocr-panel">
            <p class="ocr-panel-lead ocr-panel-lead--flush">
              Select files to process, delete, or re-queue from completed below.
            </p>
            <v-switch
              v-model="detectTables"
              class="ocr-detect-tables-switch"
              color="primary"
              density="compact"
              hide-details
              label="Detect tables"
            />
            <p class="ocr-upload-hint">
              When off, extracted text is fit to a single table per page (prototype default: on).
            </p>
            <div class="ocr-panel-actions ocr-panel-actions--stack">
              <v-btn
                color="primary"
                variant="flat"
                :disabled="!selectedUploaded.length || processing"
                :loading="processing && !!selectedUploaded.length"
                @click="processSelected"
              >
                Process selected ({{ selectedUploaded.length }})
              </v-btn>
              <v-btn
                variant="tonal"
                :disabled="processing"
                :loading="processing && !selectedUploaded.length"
                @click="startProcessingAll"
              >
                Process all uploaded
              </v-btn>
              <v-btn
                variant="outlined"
                :disabled="!selectedUploaded.length || deleting"
                :loading="deleting"
                @click="deleteSelectedUploaded"
              >
                Delete selected
              </v-btn>
            </div>
          </div>

          <div class="ocr-upload-lists">
            <OcrFileList
              title="Processing queue"
              bucket="processing"
              :names="queuedFiles"
              :active-name="jobStatus?.current_file || ''"
              :refresh-key="listKey"
            />
            <div class="ocr-upload-completed">
              <OcrFileList
                title="Completed"
                bucket="completed"
                selectable
                :selected="selectedCompleted"
                :refresh-key="listKey"
                @update:selected="selectedCompleted = $event"
              />
              <div class="ocr-upload-workspace-actions ocr-panel">
                <p class="ocr-panel-lead ocr-panel-lead--flush">
                  Move completed files back to uploaded so they can be processed again.
                </p>
                <v-btn
                  color="primary"
                  variant="flat"
                  :disabled="!selectedCompleted.length || requeueing"
                  :loading="requeueing"
                  @click="requeueCompleted"
                >
                  Reprocess selected ({{ selectedCompleted.length }})
                </v-btn>
              </div>
            </div>
          </div>
        </v-window-item>

        <v-window-item value="excel">
          <OcrExcelPanel :list-key="listKey" @refresh="listKey += 1" />
        </v-window-item>
      </v-window>
    </div>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";
import OcrExcelPanel from "@/components/layout/OCR/OcrExcelPanel.vue";
import OcrFileList from "@/components/layout/OCR/OcrFileList.vue";
import OcrProcessStatus from "@/components/layout/OCR/OcrProcessStatus.vue";
import OcrTabs from "@/components/layout/OCR/OcrTabs.vue";
import OcrUploadFileGrid from "@/components/layout/OCR/OcrUploadFileGrid.vue";
import OcrUploadPanel from "@/components/layout/OCR/OcrUploadPanel.vue";
import { ocrNotifyMixin } from "@/components/layout/OCR/OcrSnackbar.vue";

const TERMINAL = new Set(["completed", "completed_with_errors", "failed"]);

export default {
  name: "OcrUpload",
  mixins: [ocrNotifyMixin],
  components: {
    OcrExcelPanel,
    OcrFileList,
    OcrProcessStatus,
    OcrTabs,
    OcrUploadFileGrid,
    OcrUploadPanel,
  },
  data() {
    return {
      uploadTab: "selection",
      pickedFiles: [],
      uploadError: "",
      uploading: false,
      processing: false,
      deleting: false,
      requeueing: false,
      detectTables: true,
      selectedUploaded: [],
      selectedCompleted: [],
      jobStatus: null,
      processMessage: "",
      listKey: 0,
      processingOneFile: null,
      gridError: "",
      pollToken: 0,
    };
  },
  computed: {
    processAlertType() {
      const text = (this.processMessage || "").toLowerCase();
      if (text.includes("fail")) return "error";
      if (text.includes("complete")) return "success";
      return "info";
    },
    queuedFiles() {
      if (this.jobStatus?.queued_files) return this.jobStatus.queued_files;
      const files = this.jobStatus?.files || [];
      const done = new Set([
        ...(this.jobStatus?.success_files || []),
        ...(this.jobStatus?.failed_files || []),
      ]);
      return files.filter((name) => !done.has(name));
    },
  },
  watch: {
    uploadError(message) {
      if (message) this.ocrToast(message, "error");
    },
    gridError(message) {
      if (message) this.ocrToast(message, "error");
    },
    processMessage(message) {
      if (!message || this.jobStatus) return;
      this.$nextTick(() => {
        if (this.processMessage && !this.jobStatus) this.ocrToast(this.processMessage, this.processAlertType);
      });
    },
  },
  mounted() {
    this.resumeActiveJob();
  },
  beforeUnmount() {
    this.pollToken += 1;
  },
  methods: {
    async resumeActiveJob() {
      try {
        const { data } = await ocrApi.get("api/v1/files/process/active");
        if (!data?.running) return;
        if (data.kind === "excel") return;
        this.jobStatus = data;
        this.processing = true;
        if (data.job_id) await this.pollJob(data.job_id);
      } catch {
        /* no active job */
      } finally {
        this.processing = false;
        this.listKey += 1;
      }
    },
    async uploadFiles() {
      if (!this.pickedFiles?.length) return;
      this.uploading = true;
      this.uploadError = "";
      try {
        const form = new FormData();
        for (const f of this.pickedFiles) {
          form.append("files", f);
        }
        const { data } = await ocrApi.post("api/v1/files/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data.errors?.length) {
          this.uploadError = data.errors.join(" ");
        }
        this.pickedFiles = [];
        this.listKey += 1;
      } catch (e) {
        this.uploadError = formatOcrApiError(e, "Upload failed.");
      } finally {
        this.uploading = false;
      }
    },
    async runProcessRequest(files, { clearSelection = true } = {}) {
      this.processing = true;
      this.processMessage = "";
      this.jobStatus = null;
      try {
        const { data } = await ocrApi.post("api/v1/files/process/start", {
          files: files?.length ? files : null,
          detect_tables: this.detectTables,
        });
        this.jobStatus = data;
        this.processMessage = data.message || `Queued ${data.file_count || 0} file(s).`;
        if (data.job_id) {
          await this.pollJob(data.job_id);
        }
        if (clearSelection) this.selectedUploaded = [];
        this.listKey += 1;
      } catch (e) {
        this.processMessage = formatOcrApiError(e, "Processing request failed.");
        this.jobStatus = null;
      } finally {
        this.processing = false;
      }
    },
    processSelected() {
      if (!this.selectedUploaded.length) return;
      return this.runProcessRequest(this.selectedUploaded);
    },
    async processOne(filename) {
      this.processingOneFile = filename;
      this.gridError = "";
      try {
        await this.runProcessRequest([filename], { clearSelection: false });
        this.selectedUploaded = this.selectedUploaded.filter((f) => f !== filename);
      } finally {
        this.processingOneFile = null;
      }
    },
    startProcessingAll() {
      return this.runProcessRequest(null);
    },
    async deleteSelectedUploaded() {
      if (!this.selectedUploaded.length) return;
      this.deleting = true;
      try {
        await ocrApi.post("api/v1/files/delete", { files: this.selectedUploaded }, { params: { bucket: "uploaded" } });
        this.selectedUploaded = [];
        this.listKey += 1;
      } catch (e) {
        this.processMessage = formatOcrApiError(e, "Delete failed.");
      } finally {
        this.deleting = false;
      }
    },
    async requeueCompleted() {
      if (!this.selectedCompleted.length) return;
      this.requeueing = true;
      try {
        await ocrApi.post("api/v1/files/requeue", {
          files: this.selectedCompleted,
          from_bucket: "completed",
        });
        this.selectedCompleted = [];
        this.listKey += 1;
        this.processMessage = "Moved to uploaded. Select them and click Process selected.";
        this.jobStatus = null;
      } catch (e) {
        this.processMessage = formatOcrApiError(e, "Re-queue failed.");
      } finally {
        this.requeueing = false;
      }
    },
    async pollJob(jobId) {
      const token = (this.pollToken += 1);
      while (token === this.pollToken) {
        await new Promise((r) => setTimeout(r, 1500));
        if (token !== this.pollToken) return;
        try {
          const { data } = await ocrApi.get(`api/v1/files/process/status/${jobId}`);
          this.jobStatus = data;
          this.processMessage = data.message || data.status;
          if (TERMINAL.has(data.status)) break;
        } catch (e) {
          if (e?.response?.status === 404) break;
        }
      }
      this.listKey += 1;
    },
  },
};
</script>
